import type { Editor } from "@tiptap/core";
import { TextSelection } from "@tiptap/pm/state";
import { NodeViewContent, NodeViewWrapper } from "@tiptap/react";
import type { NodeViewProps } from "@tiptap/react";
import { Plus, X } from "lucide-react";
import type { ReactNode } from "react";

import { useEditorReadOnly } from "./fields-context";

function withPos(props: NodeViewProps, action: (editor: Editor, pos: number) => void) {
	const pos = props.getPos();

	if (pos === undefined) {
		return;
	}

	action(props.editor, pos);
}

function addRow(editor: Editor, signaturePos: number) {
	const signature = editor.state.doc.nodeAt(signaturePos);

	if (signature === null) {
		return;
	}

	const { nodes } = editor.state.schema;
	const row = nodes.signatureRow.create(null, nodes.signatureSlot.create());
	const pos = signaturePos + signature.nodeSize - 1;
	const tr = editor.state.tr.insert(pos, row);

	tr.setSelection(TextSelection.create(tr.doc, pos + 2));
	editor.view.dispatch(tr.scrollIntoView());
	editor.view.focus();
}

function addSlot(editor: Editor, rowPos: number) {
	const row = editor.state.doc.nodeAt(rowPos);

	if (row === null || row.childCount >= 2) {
		return;
	}

	const slot = editor.state.schema.nodes.signatureSlot.create();
	const pos = rowPos + row.nodeSize - 1;
	const tr = editor.state.tr.insert(pos, slot);

	tr.setSelection(TextSelection.create(tr.doc, pos + 1));
	editor.view.dispatch(tr.scrollIntoView());
	editor.view.focus();
}

function removeSlot(editor: Editor, slotPos: number) {
	const slot = editor.state.doc.nodeAt(slotPos);

	if (slot === null) {
		return;
	}

	const $slot = editor.state.doc.resolve(slotPos);

	if ($slot.parent.childCount > 1) {
		dispatchDelete(editor, slotPos, slotPos + slot.nodeSize);

		return;
	}

	removeRow(editor, $slot.before());
}

function removeRow(editor: Editor, rowPos: number) {
	const row = editor.state.doc.nodeAt(rowPos);

	if (row === null) {
		return;
	}

	const $row = editor.state.doc.resolve(rowPos);

	if ($row.parent.childCount > 1) {
		dispatchDelete(editor, rowPos, rowPos + row.nodeSize);

		return;
	}

	const signaturePos = $row.before();

	dispatchDelete(editor, signaturePos, signaturePos + $row.parent.nodeSize);
}

function dispatchDelete(editor: Editor, from: number, to: number) {
	editor.view.dispatch(editor.state.tr.delete(from, to).scrollIntoView());
	editor.view.focus();
}

export function SignatureView(props: NodeViewProps) {
	const readOnly = useEditorReadOnly();

	return (
		<NodeViewWrapper className="signature group/signature relative">
			<NodeViewContent as="div" />

			{!readOnly && (
				<div
					data-slot="signature-actions"
					contentEditable={false}
					className="absolute top-full left-1/2 z-20 hidden -translate-x-1/2 justify-center pt-2 group-focus-within/signature:flex group-hover/signature:flex"
				>
					<SignatureControl
						label="Adicionar linha de assinatura"
						onClick={() => withPos(props, addRow)}
					>
						<Plus />
						Linha
					</SignatureControl>
				</div>
			)}
		</NodeViewWrapper>
	);
}

export function SignatureRowView(props: NodeViewProps) {
	const readOnly = useEditorReadOnly();

	return (
		<NodeViewWrapper className="signature-row-editor group/row relative">
			<NodeViewContent as="div" className="signature-row" />

			{!readOnly && props.node.childCount < 2 && (
				<div
					data-slot="signature-row-actions"
					contentEditable={false}
					className="absolute -right-2 top-0 hidden group-focus-within/row:flex group-hover/row:flex"
				>
					<SignatureControl
						label="Adicionar assinatura ao lado"
						onClick={() => withPos(props, addSlot)}
					>
						<Plus />
					</SignatureControl>
				</div>
			)}
		</NodeViewWrapper>
	);
}

export function SignatureSlotView(props: NodeViewProps) {
	const readOnly = useEditorReadOnly();

	return (
		<NodeViewWrapper className="signature-slot group/slot relative">
			<div className="signature-line" contentEditable={false} />
			<NodeViewContent<"p"> as="p" className="signature-label" />

			{!readOnly && (
				<button
					type="button"
					data-slot="signature-remove"
					aria-label="Remover assinatura"
					contentEditable={false}
					onMouseDown={(event) => event.preventDefault()}
					onClick={() => withPos(props, removeSlot)}
					className="absolute -right-1.5 -top-2 hidden size-5 items-center justify-center rounded-full border border-border bg-card text-foreground/60 shadow-card transition-colors hover:bg-foreground/5 hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring group-hover/slot:flex [&_svg]:size-3"
				>
					<X />
				</button>
			)}
		</NodeViewWrapper>
	);
}

function SignatureControl({
	label,
	onClick,
	children,
}: {
	label: string;
	onClick(): void;
	children: ReactNode;
}) {
	return (
		<button
			type="button"
			aria-label={label}
			onMouseDown={(event) => event.preventDefault()}
			onClick={onClick}
			className="flex items-center gap-1 rounded-md border border-border bg-card px-2 py-1 font-sans text-xs text-foreground/70 shadow-card transition-colors hover:bg-foreground/5 hover:text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring [&_svg]:size-3"
		>
			{children}
		</button>
	);
}
